import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Grid, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Chip,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import GoogleIcon from '@mui/icons-material/Google';
import WebIcon from '@mui/icons-material/Web';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { format, startOfDay, endOfDay } from 'date-fns';

import Api from '../../Services/Api';
import { PageHeader, RsocNavHeader } from './';
import MainLayoutDemand from '../MainLoyout/MainLayoutDemand';

/**
 * Simplified Parking Events Component - Displays parked domain events data
 * Focused on SubID metrics
 */
const ParkingEvents = () => {
  // State for report data and UI controls
  const [reportData, setReportData] = useState(null);
  const [allData, setAllData] = useState(null); // Store all data when fetched
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [allPagesFetched, setAllPagesFetched] = useState(false);
  
  // State for filters
  const [startDateTime, setStartDateTime] = useState(() => {
    return startOfDay(new Date());
  });
  
  const [endDateTime, setEndDateTime] = useState(() => {
    return endOfDay(new Date());
  });
  
  const [subid2Filter, setSubid2Filter] = useState('');
  const [subid4Filter, setSubid4Filter] = useState('');
  const [subid5Filter, setSubid5Filter] = useState('');
  
  // State for hour range filtering
  const [fromHour, setFromHour] = useState('');
  const [toHour, setToHour] = useState('');
  
  // State for hourly chart
  const [hourlyData, setHourlyData] = useState(null);
  const [showHourlyChart, setShowHourlyChart] = useState(false);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  
  // State for sorting
  const [sortField, setSortField] = useState('estimated_revenue');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // State for selected columns - simplified default set
  const [selectedColumns, setSelectedColumns] = useState([
    'subid_1',
    'subid_2',
    'visits',
    'clicks',
    'ctr',
    'epc',
    'rpm',
    'estimated_revenue'
  ]);
  
  // State for group by field - default to combined SubIDs
  const [groupByField, setGroupByField] = useState('subid_combo');
  
  // Add state for showing all results
  const [showAllResults, setShowAllResults] = useState(true);
  
  // Define a mapping of frontend column names to backend column names
  const columnMapping = {
    'payout': 'epc',          // 'payout' is actually 'epc' in the backend
    'revenue': 'estimated_revenue',
    'impressions': 'visits',
    'campaign_id': 'subid_2', 
    'site_name': 'subid_3'
  };

  // Helper function to convert frontend column names to backend column names
  const mapColumnName = (frontendColumnName) => {
    return columnMapping[frontendColumnName] || frontendColumnName;
  };
  
  // Function to validate columns against the API's accepted list
  const validateColumns = (columns) => {
    const validColumns = [
      'public_id', 'uuid', 'server_datetime', 'domain_name', 'country_code', 
      'type', 'ip_address', 'ref_adnetwork', 'ref_keyword', 'ref_pubsite', 
      'page_query', 'subids', 'subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5',
      'visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'
    ];
    
    return columns.filter(column => validColumns.includes(column));
  };
  
  // Function to validate group_by fields against API's accepted list
  const validateGroupBy = (groupByArray) => {
    // Valid columns for grouping
    const validGroupByColumns = [
      'subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5', 'domain_name', 'country_code'
    ];
    
    return groupByArray.filter(column => validGroupByColumns.includes(column));
  };
  
  // Function to validate columns for ungrouped data
  const validateColumnsForUngrouped = (columns) => {
    const validColumns = [
      'public_id', 'uuid', 'server_datetime', 'domain_name', 'country_code', 
      'type', 'ip_address', 'ref_adnetwork', 'ref_keyword', 'ref_pubsite', 
      'page_query', 'subids', 'subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5',
      'visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'
    ];
    
    return columns.filter(column => validColumns.includes(column));
  };
  
  // Function to validate columns more strictly for grouped data
  const validateColumnsStrict = (columns) => {
    const validColumns = [
      'public_id', 'uuid', 'server_datetime', 'domain_name', 'country_code', 
      'type', 'ip_address', 'ref_adnetwork', 'ref_keyword', 'ref_pubsite', 
      'page_query', 'subids', 'subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5',
      'visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'
    ];
    
    return columns.filter(column => validColumns.includes(column));
  };
  

  // Build filters based on current state
  const buildFilters = () => {
    const filters = [];
    
    // Add date range filter with hour filtering
    if (startDateTime && endDateTime) {
      let startDate = startDateTime;
      let endDate = endDateTime;
      
      // Apply hour filtering if specified
      if (fromHour !== '' || toHour !== '') {
        const startHour = fromHour !== '' ? parseInt(fromHour) : 0;
        const endHour = toHour !== '' ? parseInt(toHour) : 23;
        
        // Create new dates with specific hours
        startDate = new Date(startDateTime);
        startDate.setHours(startHour, 0, 0, 0);
        
        endDate = new Date(endDateTime);
        endDate.setHours(endHour, 59, 59, 999);
        
        // If filtering spans multiple days but with hour range, 
        // we need to handle each day properly
        if (startDate.toDateString() !== endDate.toDateString() && (fromHour !== '' && toHour !== '')) {
          // For multi-day ranges with hour filters, we might need additional logic
          // For now, this works for single day or when filtering by hours within the date range
        }
      }
      
      filters.push({
        column: "server_datetime",
        operator: "between",
        value: [
          format(startDate, 'yyyy-MM-dd HH:mm:ss'),
          format(endDate, 'yyyy-MM-dd HH:mm:ss')
        ]
      });
    }
    
    // Add subid_2 filter if specified
    if (subid2Filter) {
      filters.push({
        column: "subid_2",
        operator: "like",
        value: `%${subid2Filter}%`
      });
    }
    
    // Add subid_4 filter if specified
    if (subid4Filter) {
      filters.push({
        column: "subid_4",
        operator: "like",
        value: `%${subid4Filter}%`
      });
    }
    
    // Add subid_5 filter if specified
    if (subid5Filter) {
      filters.push({
        column: "subid_5",
        operator: "like",
        value: `%${subid5Filter}%`
      });
    }
    
    return filters;
  };
  
  // Function to fetch parking events data
  const fetchParkingEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current state values to avoid closure issues
      const currentGroupByField = groupByField;
      const currentSelectedColumns = [...selectedColumns];
      const currentPage = page;
      const currentRowsPerPage = rowsPerPage;
      const currentSortField = sortField;
      const currentSortDirection = sortDirection;
      const currentShowAllResults = showAllResults;
      
      console.log("Fetching data with group by:", currentGroupByField);
      
      // Start with a fresh column list based on the current grouping
      let columnsToUse = [];
      
      // STEP 1: Add the group column(s) first
      if (currentGroupByField === 'subid_combo') {
        // For combo grouping, include both subid 1 and 2 columns
        columnsToUse.push('subid_1', 'subid_2');
      } else if (currentGroupByField === 'subid_2_4') {
        // For SubID 2 + SubID 4 grouping, include both columns
        columnsToUse.push('subid_2', 'subid_4');
      } else if (currentGroupByField === 'subid_combo_all') {
        // Include subid_1, subid_2, and subid_3 for this grouping
        columnsToUse.push('subid_1', 'subid_2', 'subid_3');
      } else if (currentGroupByField === 'subid_combo_full') {
        // Include all subid fields for this grouping
        columnsToUse.push('subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5');
      } else {
        // For single column grouping, include only that column (if valid)
        if (['subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5', 'domain_name', 'country_code'].includes(currentGroupByField)) {
          columnsToUse.push(currentGroupByField);
        } else {
          // Fall back to subid_2 for unsupported single groupings
          columnsToUse.push('subid_2');
        }
      }
      
      // STEP 2: Add other metric columns that should always be included
      const metricColumns = ['visits', 'clicks', 'ctr', 'epc', 'rpm', 'estimated_revenue'];
      columnsToUse.push(...metricColumns);
      
      // STEP 3: Add any additional selected columns that don't conflict with grouping
      currentSelectedColumns.forEach(col => {
        // Skip invalid columns that cause API errors
        if (['subid_3', 'subid_4', 'subid_5'].includes(col)) {
          console.log(`Excluding ${col} as it's not supported by the API`);
          return;
        }
        
        // Skip columns that would conflict with grouping
        if (col === 'subid_1' && (currentGroupByField === 'subid_2')) {
          console.log("Excluding subid_1 for subid_2 grouping");
          return;
        }
        if (col === 'subid_2' && (currentGroupByField === 'subid_1')) {
          console.log("Excluding subid_2 for subid_1 grouping");
          return;
        }
        
        // Skip columns we've already added
        if (!columnsToUse.includes(col)) {
          columnsToUse.push(col);
        }
      });
      
      // Determine group_by array based on the grouping field
      let groupByArray;
      
      if (currentGroupByField === 'subid_combo') {
        groupByArray = ['subid_1', 'subid_2'];
      } else if (currentGroupByField === 'subid_2_4') {
        // Group by SubID 2 and SubID 4
        groupByArray = ['subid_2', 'subid_4'];
      } else if (currentGroupByField === 'subid_combo_all') {
        groupByArray = ['subid_1', 'subid_2', 'subid_3'];
      } else if (currentGroupByField === 'subid_combo_full') {
        groupByArray = ['subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5'];
      } else {
        groupByArray = [currentGroupByField];
      }
      
      // Validate the group_by array (skip validation if intentionally empty for ungrouped data)
      if (groupByArray.length > 0) {
        const originalGroupBy = [...groupByArray];
        groupByArray = validateGroupBy(groupByArray);
        
        // If group_by was filtered and is now empty, fall back to subid_2
        if (originalGroupBy.length > 0 && groupByArray.length === 0) {
          console.warn('All group_by fields were invalid, falling back to subid_2');
          groupByArray = ['subid_2'];
        }
      } else {
        console.log('Using empty group_by for ungrouped individual records (subid_2_4 grouping)');
      }
      
      console.log("Group by array after validation:", groupByArray);
      
      // STEP 4: Validate final column list - use different validation based on grouping
      if (groupByArray.length === 0 && currentGroupByField === 'subid_2_4') {
        // For ungrouped subid_2_4 data, try less strict validation to allow subid_3 and subid_4
        columnsToUse = validateColumnsForUngrouped(columnsToUse);
        console.log("Using ungrouped column validation for subid_2_4");
      } else {
        // For grouped data, use strict validation
        columnsToUse = validateColumnsStrict(columnsToUse);
        console.log("Using strict column validation");
        
        // IMPORTANT: Re-apply grouping constraints after validation
        // The validation functions don't know about grouping context, so we need to re-exclude conflicting columns
        if (currentGroupByField === 'subid_1') {
          columnsToUse = columnsToUse.filter(col => col !== 'subid_2');
          console.log("Re-excluded subid_2 after validation for subid_1 grouping");
        } else if (currentGroupByField === 'subid_2') {
          columnsToUse = columnsToUse.filter(col => col !== 'subid_1');
          console.log("Re-excluded subid_1 after validation for subid_2 grouping");
        }
      }
      
      // FINAL CHECK: Log the exact columns we'll be using
      console.log("Final columns for API request:", columnsToUse);
      
      // IMPORTANT: First check if the current sort field is a special front-end only column
      // like 'impressions' or 'revenue' that needs mapping
      let frontendSortField = currentSortField;
      const specialMappings = {
        'impressions': 'visits',
        'revenue': 'estimated_revenue'
      };
      
      if (specialMappings[frontendSortField]) {
        frontendSortField = specialMappings[frontendSortField];
        console.log(`Mapped special frontend column ${currentSortField} to ${frontendSortField}`);
      }
      
      // Ensure the sort field is valid and included in the columns
      // If not, use a fallback (visits, clicks, or estimated_revenue)
      let validSortField = frontendSortField;
      if (!columnsToUse.includes(frontendSortField)) {
        // Try to use estimated_revenue, clicks, or visits as fallback
        if (columnsToUse.includes('estimated_revenue')) {
          validSortField = 'estimated_revenue';
        } else if (columnsToUse.includes('clicks')) {
          validSortField = 'clicks';
        } else if (columnsToUse.includes('visits')) {
          validSortField = 'visits';
        } else {
          // Use the first valid column as a last resort
          validSortField = columnsToUse[0];
        }
        console.warn(`Sort field "${frontendSortField}" not in columns, using "${validSortField}" instead`);
      }
      
      // Map the valid sort field to the correct API column name
      // For frontend-only calculated columns, fallback to a valid API column
      let apiSortField;
      if (['cpc', 'fb_ctr'].includes(validSortField)) {
        // These are frontend-only calculated columns, fallback to 'clicks' for API sorting
        apiSortField = 'clicks';
        console.log(`Frontend-only sort field "${validSortField}" mapped to API fallback: ${apiSortField}`);
      } else {
        apiSortField = mapColumnName(validSortField);
      }
      console.log(`Sort field mapping: ${currentSortField} → ${frontendSortField} → ${validSortField} → ${apiSortField}`);
      
      // Create request payload
      const requestPayload = {
        columns: columnsToUse,
        filter_by: buildFilters(),
        order_by: [{
          column: apiSortField,
          order: currentSortDirection
        }],
        page: currentPage + 1, // API uses 1-based indexing
        per_page: currentRowsPerPage,
        skip_pagination: !currentShowAllResults // If showing all results, don't skip pagination
      };
      
      // Only include group_by if it's not empty (some APIs don't like empty arrays)
      if (groupByArray.length > 0) {
        requestPayload.group_by = groupByArray;
      }
      
      console.debug('Parking Events request payload:', requestPayload);
      
      const response = await Api.getParkingEvents(requestPayload);
      
      if (response && response.data) {
        console.log("Received data:", response.data.length, "items");
        
        let processedData = response.data;
        
        // Update selected columns to match what we actually requested
        setSelectedColumns(columnsToUse);
        
        // Check if we have all pages fetched
        if (response.current_page === 1 && response.last_page === 1 && response.total > response.per_page) {
          console.log("All pages fetched and combined");
          setAllPagesFetched(true);
          setAllData(processedData); // Store the complete processed dataset
          
          // Calculate the current visible page of data
          const start = currentPage * currentRowsPerPage;
          const end = start + currentRowsPerPage;
          const visibleData = processedData.slice(start, end);
          
          setReportData(visibleData);
        } else {
          setAllPagesFetched(false);
          setAllData(null);
          setReportData(processedData);
        }
        
        setTotalItems(response.total || processedData.length);
      } else {
        console.error("API response missing data:", response);
        setError('Failed to fetch parking events data');
      }
    } catch (err) {
      console.error('Error fetching parking events:', err);
      setError(err.message || 'Failed to fetch parking events data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load initial data on component mount
  useEffect(() => {
    fetchParkingEvents();
  }, []);
  
  // Function to handle page change - modify to handle client-side pagination when all data is available
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    
    // If all pages are fetched, do client-side pagination
    if (allPagesFetched && allData) {
      const start = newPage * rowsPerPage;
      const end = start + rowsPerPage;
      const visibleData = allData.slice(start, end);
      setReportData(visibleData);
    } else {
      // Otherwise, fetch from the server
      setTimeout(() => {
        fetchParkingEvents();
      }, 0);
    }
  };
  
  // Function to handle rows per page change - modify to handle client-side pagination
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    
    // If all pages are fetched, do client-side pagination
    if (allPagesFetched && allData) {
      const visibleData = allData.slice(0, newRowsPerPage);
      setReportData(visibleData);
    } else {
      // Otherwise, fetch from the server
      setTimeout(() => {
        fetchParkingEvents();
      }, 0);
    }
  };
  
  // Modify handleSearch to reset pagination state
  const handleSearch = () => {
    setPage(0); // Reset to first page
    setLoading(true);
    setReportData(null); // Clear current data
    setAllData(null); // Clear all data
    setAllPagesFetched(false); // Reset this state since we're performing a new search
    
    // Use setTimeout to ensure state updates have been processed
    setTimeout(() => {
      fetchParkingEvents()
        .catch(error => {
          console.error("Search error:", error);
          setError(error.message || "Failed to search");
          setLoading(false);
        });
    }, 50);
  };
  
  // Modify handleSortChange to always perform client-side sorting without reloading data
  const handleSortChange = (field) => {
    console.log(`Sorting by ${field}, current value: ${sortField}, direction: ${sortDirection}`);
    
    const newDirection = field === sortField 
      ? (sortDirection === 'asc' ? 'desc' : 'asc') 
      : 'desc';
    
    console.log(`New sort direction will be: ${newDirection}`);
    
    setSortField(field);
    setSortDirection(newDirection);
    
    // Log a row of data to see what we're working with
    if (reportData && reportData.length > 0) {
      console.log("Sample row for sorting:", reportData[0]);
    }
    
    // Always perform client-side sorting for instant feedback - no need to fetch from server
    if (reportData && reportData.length > 0) {
      // Sort the current visible data
      const sortedData = [...reportData].sort((a, b) => {
        // Extract values, ensuring we handle all possible formats and null/undefined values
        let valA, valB;
        
        // Special case for 'impressions' which might be mapped to 'visits'
        if (field === 'impressions') {
          valA = parseFloat(a['visits'] || 0);
          valB = parseFloat(b['visits'] || 0);
        } 
        // Handle CVR which should always be numeric and might be stored as string with % symbol
        else if (field === 'ctr') {
          // Remove '%' if present and convert to number
          // Log the raw values for debugging
          console.log("CVR values:", {
            a_raw: a[field],
            b_raw: b[field],
            a_type: typeof a[field],
            b_type: typeof b[field]
          });
          
          // Handle various formats of CVR: number, string with %, or string without %
          valA = typeof a[field] === 'string' 
            ? parseFloat(a[field].replace('%', '')) 
            : parseFloat(a[field] || 0);
          
          valB = typeof b[field] === 'string' 
            ? parseFloat(b[field].replace('%', '')) 
            : parseFloat(b[field] || 0);
          
          console.log("Parsed CTR values:", { valA, valB });
        }
        // Handle other numeric fields
        else if (['visits', 'clicks', 'estimated_revenue', 'rpm', 'epc'].includes(field)) {
          valA = parseFloat(a[field] || 0);
          valB = parseFloat(b[field] || 0);
        }
        // Handle string values or other fields
        else {
          valA = a[field] || '';
          valB = b[field] || '';
          
          // For strings, use localeCompare
          if (typeof valA === 'string' && typeof valB === 'string') {
            return newDirection === 'asc' 
              ? valA.localeCompare(valB) 
              : valB.localeCompare(valA);
          }
        }
        
        // For numeric values
        if (!isNaN(valA) && !isNaN(valB)) {
          return newDirection === 'asc' ? valA - valB : valB - valA;
        }
        
        // Fallback for mixed types or other issues
        return newDirection === 'asc' 
          ? String(valA).localeCompare(String(valB)) 
          : String(valB).localeCompare(String(valA));
      });
      
      // Update the current visible data immediately for instant feedback
      setReportData(sortedData);
    }
    
    // If all pages are fetched, also sort the full dataset
    if (allPagesFetched && allData) {
      // Sort the full dataset using the same logic as above
      const sortedAllData = [...allData].sort((a, b) => {
        // Extract values, ensuring we handle all possible formats and null/undefined values
        let valA, valB;
        
        // Special case for 'impressions' which might be mapped to 'visits'
        if (field === 'impressions') {
          valA = parseFloat(a['visits'] || 0);
          valB = parseFloat(b['visits'] || 0);
        } 
        // Handle CVR which should always be numeric and might be stored as string with % symbol
        else if (field === 'ctr') {
          // Handle various formats of CVR: number, string with %, or string without %
          valA = typeof a[field] === 'string' 
            ? parseFloat(a[field].replace('%', '')) 
            : parseFloat(a[field] || 0);
          
          valB = typeof b[field] === 'string' 
            ? parseFloat(b[field].replace('%', '')) 
            : parseFloat(b[field] || 0);
        }
        // Handle other numeric fields
        else if (['visits', 'clicks', 'estimated_revenue', 'rpm', 'epc'].includes(field)) {
          valA = parseFloat(a[field] || 0);
          valB = parseFloat(b[field] || 0);
        }
        // Handle string values or other fields
        else {
          valA = a[field] || '';
          valB = b[field] || '';
          
          // For strings, use localeCompare
          if (typeof valA === 'string' && typeof valB === 'string') {
            return newDirection === 'asc' 
              ? valA.localeCompare(valB) 
              : valB.localeCompare(valA);
          }
        }
        
        // For numeric values
        if (!isNaN(valA) && !isNaN(valB)) {
          return newDirection === 'asc' ? valA - valB : valB - valA;
        }
        
        // Fallback for mixed types or other issues
        return newDirection === 'asc' 
          ? String(valA).localeCompare(String(valB)) 
          : String(valB).localeCompare(String(valA));
      });
      
      setAllData(sortedAllData);
      
      // Reset to first page and update visible slice
      setPage(0);
      setReportData(sortedAllData.slice(0, rowsPerPage));
    }
    
    // We never need to fetch from server when sorting - we've already sorted the data client-side
  };
  
  // Function to handle CSV export
  const handleExportCsv = () => {
    try {
      setLoading(true);
      
      // Use current state values
      const currentGroupByField = groupByField;
      const currentSelectedColumns = [...selectedColumns];
      
      // Adjust columns based on groupByField to avoid invalid column combinations
      let adjustedColumns = [...currentSelectedColumns];
      
      // When grouping by a specific subid, exclude the other subids from columns
      if (currentGroupByField === 'subid_1') {
        adjustedColumns = adjustedColumns.filter(col => col !== 'subid_2' && col !== 'subid_3');
      } else if (currentGroupByField === 'subid_2') {
        adjustedColumns = adjustedColumns.filter(col => col !== 'subid_1' && col !== 'subid_3');
      } else if (currentGroupByField === 'subid_3') {
        adjustedColumns = adjustedColumns.filter(col => col !== 'subid_1' && col !== 'subid_2');
      } else if (currentGroupByField === 'subid_2_4') {
        adjustedColumns = adjustedColumns.filter(col => col !== 'subid_1' && col !== 'subid_3' && col !== 'subid_5');
      }
      
      // Determine group_by array based on the grouping field
      let groupByArray;
      if (currentGroupByField === 'subid_combo') {
        groupByArray = ['subid_1', 'subid_2'];
      } else if (currentGroupByField === 'subid_2_4') {
        // Group by SubID 2 and SubID 4
        groupByArray = ['subid_2', 'subid_4'];
      } else if (currentGroupByField === 'subid_combo_all') {
        groupByArray = ['subid_1', 'subid_2', 'subid_3'];
      } else if (currentGroupByField === 'subid_combo_full') {
        groupByArray = ['subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5'];
      } else {
        groupByArray = [currentGroupByField];
      }
      
      // Validate the group_by array (skip validation if intentionally empty for ungrouped data)
      if (groupByArray.length > 0) {
        const originalGroupBy = [...groupByArray];
        groupByArray = validateGroupBy(groupByArray);
        
        // If group_by was filtered and is now empty, fall back to subid_2
        if (originalGroupBy.length > 0 && groupByArray.length === 0) {
          console.warn('All group_by fields were invalid, falling back to subid_2');
          groupByArray = ['subid_2'];
        }
      } else {
        console.log('Export: Using empty group_by for ungrouped individual records (subid_2_4 grouping)');
      }
      
      // Validate columns
      const validColumns = validateColumns(adjustedColumns);
      
      // Ensure the sort field is valid and included in the columns
      let validSortField = sortField;
      if (!validColumns.includes(sortField)) {
        // Try to use estimated_revenue, clicks, or visits as fallback
        if (validColumns.includes('estimated_revenue')) {
          validSortField = 'estimated_revenue';
        } else if (validColumns.includes('clicks')) {
          validSortField = 'clicks';
        } else if (validColumns.includes('visits')) {
          validSortField = 'visits';
        } else {
          // Use the first valid column as a last resort
          validSortField = validColumns[0];
        }
        console.warn(`Export: Sort field "${sortField}" not in columns, using "${validSortField}" instead`);
      }
      
      // Map the valid sort field to the correct API column name
      // For frontend-only calculated columns, fallback to a valid API column
      let apiSortField;
      if (['cpc', 'fb_ctr'].includes(validSortField)) {
        // These are frontend-only calculated columns, fallback to 'clicks' for API sorting
        apiSortField = 'clicks';
        console.log(`Export: Frontend-only sort field "${validSortField}" mapped to API fallback: ${apiSortField}`);
      } else {
        apiSortField = mapColumnName(validSortField);
      }
      console.log(`Export sort field mapping: ${sortField} → ${validSortField} → ${apiSortField}`);
      
      // Create export request payload with adjusted columns
      const exportPayload = {
        columns: validColumns,
        filter_by: buildFilters(),
        order_by: [{
          column: apiSortField,
          order: sortDirection
        }],
        format: 'csv',
        filename: `parking-events-${format(new Date(), 'yyyy-MM-dd')}`
      };
      
      // Only include group_by if it's not empty (some APIs don't like empty arrays)
      if (groupByArray.length > 0) {
        exportPayload.group_by = groupByArray;
      }
      
      Api.getParkingEvents(exportPayload)
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      setError(err.message || 'Failed to export data');
      setLoading(false);
    }
  };
  
  // Function to handle group by change
  const handleGroupByChange = (e) => {
    const newGroupByField = e.target.value;
    console.log("Group changing to:", newGroupByField);
    
    // Set loading state immediately
    setLoading(true);
    
    // Clear current data to prevent showing incorrect data during transition
    setReportData(null);
    
    // Use React state updater function to ensure we have the latest state
    setGroupByField(newGroupByField);
    setPage(0);
    
    // Create a separate async function to handle the fetch with the new group
    const refreshWithNewGroup = async () => {
      try {
        // Build filters based on current state (and form values)
        const currentFilters = buildFilters();
        
        // Start with a fresh column list for the new grouping
        let columnsToUse = [];
        
        // Add the group column(s) first
        if (newGroupByField === 'subid_combo') {
          columnsToUse.push('subid_1', 'subid_2');
        } else if (newGroupByField === 'subid_2_4') {
          columnsToUse.push('subid_2', 'subid_4');
        } else if (newGroupByField === 'subid_combo_all') {
          columnsToUse.push('subid_1', 'subid_2', 'subid_3');
        } else if (newGroupByField === 'subid_combo_full') {
          columnsToUse.push('subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5');
        } else {
          columnsToUse.push(newGroupByField);
        }
        
        // Add metric columns
        const metricColumns = ['visits', 'clicks', 'ctr', 'epc', 'rpm', 'estimated_revenue'];
        columnsToUse.push(...metricColumns);
        
        // Validate
        columnsToUse = validateColumns(columnsToUse);
        
        // Determine the group_by based on the new field
        let groupByToUse;
        if (newGroupByField === 'subid_combo') {
          groupByToUse = ['subid_1', 'subid_2'];
        } else if (newGroupByField === 'subid_2_4') {
          groupByToUse = ['subid_2', 'subid_4'];
        } else if (newGroupByField === 'subid_combo_all') {
          groupByToUse = ['subid_1', 'subid_2', 'subid_3'];
        } else if (newGroupByField === 'subid_combo_full') {
          groupByToUse = ['subid_1', 'subid_2', 'subid_3', 'subid_4', 'subid_5'];
        } else {
          groupByToUse = [newGroupByField];
        }
        
        // Create request with the new grouping (using exact values, not state)
        const requestPayload = {
          columns: columnsToUse,
          filter_by: currentFilters,
          order_by: [{
            column: sortField,
            order: sortDirection
          }],
          page: 1, // Reset to page 1
          per_page: rowsPerPage
        };
        
        // Only include group_by if it's not empty
        if (groupByToUse.length > 0) {
          requestPayload.group_by = groupByToUse;
        }
        
        console.log("Group change request payload:", requestPayload);
        
        // Make direct API call
        const response = await Api.getParkingEvents(requestPayload);
        
        if (response && response.data) {
          console.log("Received new group data:", response.data.length, "items");
          
          let processedData = response.data;
          
          // Update selected columns to match what we actually requested
          setSelectedColumns(columnsToUse);
          
          // Update all relevant state
          setReportData(processedData);
          setTotalItems(response.total || processedData.length);
          setError(null);
        } else {
          console.error("Failed to get data for new group");
          setError('Failed to fetch data for the selected grouping');
        }
      } catch (err) {
        console.error("Error during group change:", err);
        setError(err.message || 'Failed to update grouping');
      } finally {
        setLoading(false);
      }
    };
    
    // Execute the refresh function
    refreshWithNewGroup();
  };
  
  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!reportData || reportData.length === 0) {
      return {
        totalVisits: 0,
        totalClicks: 0,
        averageCtr: 0,
        totalRevenue: 0
      };
    }
    
    let totalVisits = 0;
    let totalClicks = 0;
    let totalRevenue = 0;
    
    reportData.forEach(item => {
      totalVisits += parseInt(item.visits || 0, 10);
      totalClicks += parseInt(item.clicks || 0, 10);
      totalRevenue += parseFloat(item.estimated_revenue || 0);
    });
    
    const averageCtr = totalVisits > 0 ? (totalClicks / totalVisits) * 100 : 0;
    
    return {
      totalVisits,
      totalClicks,
      averageCtr,
      totalRevenue
    };
  }, [reportData]);
  
  // Render summary cards
  const renderSummaryCards = () => {
    const { totalVisits, totalClicks, averageCtr, totalRevenue } = summaryMetrics;
    
    // Calculate average EPC
    const averageEpc = totalClicks > 0 ? totalRevenue / totalClicks : 0;
    
    return (
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>EPC</Typography>
              <Typography variant="h5">${averageEpc.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Visits</Typography>
              <Typography variant="h5">{totalVisits.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Clicks</Typography>
              <Typography variant="h5">{totalClicks.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Revenue</Typography>
              <Typography variant="h5">${totalRevenue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Modify the campaign type detection function
  const getCampaignType = (subid2) => {
    if (!subid2) return null;
    
    // More than 8 characters is a Google GDN campaign
    if (subid2.length > 8) {
      return {
        type: 'google',
        tooltip: 'Google GDN Campaign'
      };
    } 
    // Less than or equal to 8 characters is Taboola
    else if (subid2.length > 0) {
      return {
        type: 'taboola',
        tooltip: 'Taboola Campaign'
      };
    }
    
    return null;
  };
  
  // Create handlers for date changes
  const handleStartDateChange = (newDate) => {
    if (newDate) {
      setStartDateTime(startOfDay(newDate));
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate) {
      setEndDateTime(endOfDay(newDate));
    }
  };
  
  // Handlers for hour range changes
  const handleFromHourChange = (event) => {
    setFromHour(event.target.value);
  };
  
  const handleToHourChange = (event) => {
    setToHour(event.target.value);
  };
  
  // Function to generate hourly chart
  const generateHourlyChart = async () => {
    setHourlyLoading(true);
    setError(null);
    
    try {
      const params = {
        start_date: format(startDateTime, 'yyyy-MM-dd'),
        end_date: format(endDateTime, 'yyyy-MM-dd')
      };
      
      // Add filters if specified
      if (subid2Filter) {
        params.subid_2 = subid2Filter;
      }
      if (subid4Filter) {
        params.subid_4 = subid4Filter;
      }
      if (subid5Filter) {
        params.subid_5 = subid5Filter;
      }
      
      const response = await Api.getParkingEventsHourly(params);
      
      if (response.success) {
        setHourlyData(response.data);
        setShowHourlyChart(true);
      } else {
        setError('Failed to fetch hourly data: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating hourly chart:', error);
      setError('An error occurred while generating the hourly chart');
    } finally {
      setHourlyLoading(false);
    }
  };
  
  // Modified refresh function to handle client-side pagination
  const handleRefresh = () => {
    setLoading(true);
    setAllData(null);
    setAllPagesFetched(false);
    
    // Reset sort to a safe default if the current sort field might be problematic
    // This helps prevent API errors with invalid sort columns
    const currentColumns = [...selectedColumns];
    if (!currentColumns.includes(sortField)) {
      console.log(`Current sort field ${sortField} is not in selected columns, resetting to a safe default`);
      if (currentColumns.includes('estimated_revenue')) {
        setSortField('estimated_revenue');
      } else if (currentColumns.includes('clicks')) {
        setSortField('clicks');
      } else if (currentColumns.includes('visits')) {
        setSortField('visits');
      }
    }
    
    // Use setTimeout to ensure we're not in the middle of a render cycle
    // and all state changes have been applied
    setTimeout(() => {
      fetchParkingEvents()
        .catch(error => {
          console.error("Error during refresh:", error);
          setError(error.message || "Failed to refresh data");
          setLoading(false);
        });
    }, 100); // Increased timeout to ensure state updates are processed
  };
  
  // Add handler for show all results toggle - modified
  const handleShowAllResultsChange = (event) => {
    const newValue = event.target.checked;
    setShowAllResults(newValue);
    
    // If turning on, fetch all results
    if (newValue) {
      setPage(0);
      setAllData(null);
      setAllPagesFetched(false);
      setTimeout(() => {
        fetchParkingEvents();
      }, 50);
    } else {
      // If turning off, reset the states
      setAllPagesFetched(false);
      setAllData(null);
      // Fetch just the current page
      setTimeout(() => {
        fetchParkingEvents();
      }, 50);
    }
  };
  
  return (
    <MainLayoutDemand>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <PageHeader title="SubID Metrics" />
        <RsocNavHeader />
        
        {/* Simplified Filters */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <FilterListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filters
          </Typography>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDateTime}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  maxDate={endDateTime}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDateTime}
                  onChange={handleEndDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  minDate={startDateTime}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SubID 2"
                value={subid2Filter}
                onChange={(e) => setSubid2Filter(e.target.value)}
                placeholder="Filter by SubID 2"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  label="SubID 4"
                  value={subid4Filter}
                  onChange={(e) => setSubid4Filter(e.target.value)}
                  placeholder="Filter by SubID 4"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSubid4Filter('zl')}
                  sx={{ minWidth: 'auto', height: '40px' }}
                >
                  ZL
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSubid4Filter('')}
                  sx={{ minWidth: 'auto', height: '40px' }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="SubID 5"
                value={subid5Filter}
                onChange={(e) => setSubid5Filter(e.target.value)}
                placeholder="Filter by SubID 5"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>From Hour</InputLabel>
                <Select
                  value={fromHour}
                  onChange={handleFromHourChange}
                  label="From Hour"
                >
                  <MenuItem value="">All Hours</MenuItem>
                  {Array.from({length: 24}, (_, i) => (
                    <MenuItem key={i} value={i}>{i}:00</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>To Hour</InputLabel>
                <Select
                  value={toHour}
                  onChange={handleToHourChange}
                  label="To Hour"
                >
                  <MenuItem value="">All Hours</MenuItem>
                  {Array.from({length: 24}, (_, i) => (
                    <MenuItem key={i} value={i}>{i}:59</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="group-by-label">Group By</InputLabel>
                <Select
                  labelId="group-by-label"
                  value={groupByField}
                  onChange={handleGroupByChange}
                  label="Group By"
                >
                  <MenuItem value="subid_1">SubID 1</MenuItem>
                  <MenuItem value="subid_2">SubID 2</MenuItem>
                  <MenuItem value="subid_3">SubID 3</MenuItem>
                  <MenuItem value="subid_4">SubID 4</MenuItem>
                  <MenuItem value="subid_5">SubID 5</MenuItem>
                  <MenuItem value="subid_combo">SubID 1 + SubID 2</MenuItem>
                  <MenuItem value="subid_2_4">Group by SubID 2 + SubID 4</MenuItem>
                  <MenuItem value="subid_combo_all">SubID 1 + 2 + 3</MenuItem>
                  <MenuItem value="subid_combo_full">SubID 1 + 2 + 3 + 4 + 5</MenuItem>
                  <MenuItem value="domain_name">Domain Name</MenuItem>
                  <MenuItem value="country_code">Country</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllResults}
                    onChange={handleShowAllResultsChange}
                    color="primary"
                  />
                }
                label="Fetch all pages (combines results from all pages)"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSubid2Filter('');
                setSubid4Filter('');
                setFromHour('');
                setToHour('');
              }}
              sx={{ minWidth: 100 }}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Loading...' : 'Search'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportCsv}
              startIcon={<DownloadIcon />}
              disabled={loading || !reportData}
              sx={{ minWidth: 120 }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={generateHourlyChart}
              startIcon={hourlyLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              disabled={hourlyLoading}
              sx={{ minWidth: 150 }}
            >
              {hourlyLoading ? 'Generating...' : 'Hourly Chart'}
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
        
        {/* Hourly Chart */}
        {showHourlyChart && hourlyData && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Hourly Performance Chart {startDateTime && endDateTime && 
                    `(${format(startDateTime, 'MMM dd')} - ${format(endDateTime, 'MMM dd')})`
                  }
                </Typography>
                <Button
                size="small"
                onClick={() => setShowHourlyChart(false)}
                sx={{ minWidth: 'auto' }}
              >
                Hide Chart
              </Button>
              </Box>
              
              {/* RPM Legend */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  RPM Performance:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    size="small" 
                    label="Top 25%" 
                    sx={{ 
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      color: 'success.main',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                  <Chip 
                    size="small" 
                    label="50-75%" 
                    sx={{ 
                      backgroundColor: 'rgba(255, 152, 0, 0.2)',
                      color: 'warning.main',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                  <Chip 
                    size="small" 
                    label="25-50%" 
                    sx={{ 
                      backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      color: 'info.main',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                  <Chip 
                    size="small" 
                    label="Bottom 25%" 
                    sx={{ 
                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      color: 'error.main',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  ★ = Best performing hour
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ width: '100%', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Performance metrics grouped by hour ({format(startDateTime, 'yyyy-MM-dd')} to {format(endDateTime, 'yyyy-MM-dd')})
              </Typography>
              
              {/* Table with visual indicators */}
              <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hour</TableCell>
                      <TableCell align="right">Visits</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                      <TableCell align="right">CVR (%)</TableCell>
                      <TableCell align="right">Revenue ($)</TableCell>
                      <TableCell align="right">EPC ($)</TableCell>
                      <TableCell align="right">RPM ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Calculate min and max RPM for color scaling
                      const rpmValues = hourlyData.map(h => h.rpm).filter(rpm => rpm > 0);
                      const maxRpm = Math.max(...rpmValues);
                      const minRpm = Math.min(...rpmValues);
                      const rpmRange = maxRpm - minRpm;
                      
                      // Function to get color based on RPM value
                      const getRpmColor = (rpm) => {
                        if (rpm === 0) return 'text.secondary';
                        if (rpmRange === 0) return 'success.main';
                        
                        const percentage = ((rpm - minRpm) / rpmRange) * 100;
                        
                        if (percentage >= 75) return 'success.main'; // Top 25% - Green
                        if (percentage >= 50) return 'warning.main'; // 50-75% - Orange
                        if (percentage >= 25) return 'info.main';    // 25-50% - Blue
                        return 'error.main';                          // Bottom 25% - Red
                      };
                      
                      // Function to get background color with opacity
                      const getRpmBackground = (rpm) => {
                        if (rpm === 0) return 'transparent';
                        if (rpmRange === 0) return 'rgba(76, 175, 80, 0.1)';
                        
                        const percentage = ((rpm - minRpm) / rpmRange) * 100;
                        
                        if (percentage >= 75) return 'rgba(76, 175, 80, 0.1)';  // Green
                        if (percentage >= 50) return 'rgba(255, 152, 0, 0.1)';  // Orange
                        if (percentage >= 25) return 'rgba(33, 150, 243, 0.1)'; // Blue
                        return 'rgba(244, 67, 54, 0.1)';                        // Red
                      };
                      
                      return hourlyData.map((hourData, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {hourData.datetime.split(' ')[1] || hourData.datetime}
                        </TableCell>
                        <TableCell align="right">{hourData.visits.toLocaleString()}</TableCell>
                        <TableCell align="right">{hourData.clicks.toLocaleString()}</TableCell>
                        <TableCell align="right">{hourData.ctr}%</TableCell>
                        <TableCell align="right">${hourData.estimated_revenue.toFixed(2)}</TableCell>
                        <TableCell align="right">${hourData.epc.toFixed(4)}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{
                            backgroundColor: getRpmBackground(hourData.rpm),
                            color: getRpmColor(hourData.rpm),
                            fontWeight: hourData.rpm >= maxRpm * 0.75 ? 'bold' : 'normal',
                            position: 'relative'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {/* RPM Value */}
                            <Typography variant="body2" component="span">
                              ${hourData.rpm.toFixed(4)}
                            </Typography>
                            
                            {/* Visual indicator bar */}
                            <Box
                              sx={{
                                ml: 1,
                                width: 40,
                                height: 6,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: 3,
                                overflow: 'hidden',
                                position: 'relative'
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  height: '100%',
                                  width: rpmRange > 0 ? `${((hourData.rpm - minRpm) / rpmRange) * 100}%` : '100%',
                                  backgroundColor: getRpmColor(hourData.rpm),
                                  transition: 'width 0.3s ease'
                                }}
                              />
                            </Box>
                            
                            {/* Best hour indicator */}
                            {hourData.rpm === maxRpm && hourData.rpm > 0 && (
                              <Tooltip title="Best performing hour">
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    ml: 1, 
                                    color: 'success.main',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ★
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </Box>
              
              {/* Summary */}
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Summary:</strong> {(() => {
                    const uniqueDays = new Set(hourlyData.map(h => h.datetime.split(' ')[0])).size;
                    return `${uniqueDays} day${uniqueDays > 1 ? 's' : ''}, ${hourlyData.length} hours of data`;
                  })()} | 
                  Total Visits: {hourlyData.reduce((sum, h) => sum + h.visits, 0).toLocaleString()} | 
                  Total Clicks: {hourlyData.reduce((sum, h) => sum + h.clicks, 0).toLocaleString()} | 
                  Total Revenue: ${hourlyData.reduce((sum, h) => sum + h.estimated_revenue, 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Results */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5, minHeight: '300px' }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading data...
            </Typography>
          </Box>
        ) : reportData ? (
          <>
            {/* Summary metrics */}
            {renderSummaryCards()}
            
            {/* Results table */}
            <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ p: 1 }}>
                  {allPagesFetched 
                    ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalItems)} of ${totalItems} results` 
                    : `Showing page ${page+1} of results`}
                </Typography>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'primary.main' }}>
                    <TableRow>
                      {selectedColumns.map((column) => (
                        <TableCell 
                          key={column}
                          align={['visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'].includes(column) ? 'right' : 'left'}
                          onClick={() => handleSortChange(column)}
                          sx={{ 
                            cursor: 'pointer',
                            color: 'white',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: 'primary.dark'
                            },
                            bgcolor: sortField === column ? 'primary.dark' : 'primary.main',
                            position: 'relative',
                            pr: sortField === column ? 3 : 2
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: ['visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'].includes(column) ? 'flex-end' : 'flex-start',
                            '& .sort-icon': {
                              fontSize: '1rem',
                              ml: 0.5,
                              transition: 'transform 0.2s ease-in-out'
                            }
                          }}>
                            {column === 'subid_1' ? 'SubID 1' : 
                             column === 'subid_2' ? 'SubID 2' :
                             column === 'subid_3' ? 'SubID 3' :
                             column === 'subid_4' ? 'SubID 4' :
                             column === 'subid_5' ? 'SubID 5' :
                             column === 'visits' ? 'Visits' :
                             column === 'clicks' ? 'Clicks' :
                             column === 'ctr' ? 'CVR' :
                             column === 'epc' ? 'EPC' :
                             column === 'rpm' ? 'RPM' :
                             column === 'estimated_revenue' ? 'Revenue' : 
                             column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                            
                            {sortField === column ? (
                              sortDirection === 'asc' ? (
                                <ArrowUpwardIcon className="sort-icon" />
                              ) : (
                                <ArrowDownwardIcon className="sort-icon" />
                              )
                            ) : (
                              <UnfoldMoreIcon className="sort-icon" sx={{ opacity: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.length > 0 ? (
                      reportData.map((row, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                            '&:hover': { bgcolor: 'action.selected' }
                          }}
                        >
                          {selectedColumns.map((column) => (
                            <TableCell 
                              key={column}
                              align={['visits', 'clicks', 'ctr', 'rpm', 'epc', 'estimated_revenue'].includes(column) ? 'right' : 'left'}
                            >
                              {column === 'estimated_revenue' || column === 'rpm' || column === 'epc' ? (
                                // Format currency values
                                `$${parseFloat(row[column] || 0).toFixed(2)}`
                              ) : column === 'ctr' ? (
                                // Format CTR as percentage
                                `${parseFloat(row[column] || 0).toFixed(2)}%`
                              ) : column === 'visits' || column === 'clicks' ? (
                                // Format numeric values with locale-specific formatting
                                parseInt(row[column] || 0, 10).toLocaleString()
                              ) : column === 'subid_2' ? (
                                // Display campaign icons next to subid_2
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {row[column]}
                                  {getCampaignType(row[column]) && (
                                    <Tooltip title={getCampaignType(row[column]).tooltip}>
                                      <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                                        {getCampaignType(row[column]).type === 'google' ? (
                                          <GoogleIcon sx={{ fontSize: 16, color: '#4285F4' }} />
                                        ) : (
                                          <WebIcon sx={{ fontSize: 16, color: '#FF6C36' }} />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              ) : (
                                // Default formatting
                                row[column]
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={selectedColumns.length} align="center" sx={{ py: 4 }}>
                          No data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Always show pagination - modify to reflect client-side pagination when all data is fetched */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelDisplayedRows={({ from, to, count }) => 
                  allPagesFetched 
                    ? `${from}-${to} of ${count} (local pagination)` 
                    : `${from}-${to} of ${count}`
                }
              />
            </Paper>
          </>
        ) : (
          // Empty state
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
              minHeight: 400,
              boxShadow: 1
            }}
          >
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No Data Available
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Set filters and click Search to view SubID metrics.
            </Typography>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{ minWidth: 150 }}
            >
              Search Now
            </Button>
          </Box>
        )}
      </Box>
    </MainLayoutDemand>
  );
};

export default ParkingEvents; 